import { Component, OnInit, Signal, inject, signal, DestroyRef, ChangeDetectionStrategy } from '@angular/core';
import { concatMap, Observable, tap } from 'rxjs';
import { UserState } from 'src/app/shared/states/user.state';
import { Store } from '@ngxs/store';
import { MatDialog, MatDialogRef, MatDialogTitle, MatDialogContent, MatDialogActions } from '@angular/material/dialog';
import { IImpersonation } from 'src/app/shared/interfaces/IImpersonation';
import { IImpersonationDialogDataPipe } from 'src/app/shared/interfaces/IImpersonation.module';
import { MatTabGroup, MatTab } from '@angular/material/tabs';
import { TranslatePipe, TranslateService } from '@ngx-translate/core';
import { getImpersonationColumns } from 'src/app/shared/configs/impersonation.config';
import { LoadUserImpersonationData, UpdateImpersonations } from 'src/app/shared/states/user.actions';
import { LoadingMode } from 'src/app/shared/enums/LoadingMode.enum';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { AssignImpersonatorComponent } from '../assign-impersonator-dialog/assign-impersonator-dialog.component';
import { IUser } from '../../../interfaces/IUser';
import { TableColumn, TableComponent } from '../../table/table.component';
import { LoadingComponent } from '../../loading/loading.component';

@Component({
  selector: 'app-impersonation-dialog',
  templateUrl: './impersonation-dialog.component.html',
  styleUrls: ['./impersonation-dialog.component.scss'],
  standalone: true,
  changeDetection: ChangeDetectionStrategy.Eager,
  imports: [MatDialogTitle, MatDialogContent, MatTabGroup, MatTab, MatDialogActions, TranslatePipe, TableComponent, LoadingComponent],
})
export class ImpersonationDialogComponent implements OnInit {
  private dialogRef = inject<MatDialogRef<ImpersonationDialogComponent>>(MatDialogRef);
  private dialog = inject(MatDialog);
  private store = inject(Store);
  private translateService = inject(TranslateService);
  private destroyRef = inject(DestroyRef);
  private imitableUsers$: Observable<IImpersonation[]> = this.store.select<IImpersonation[]>(UserState.imitableUsers);
  private impersonatingUsers$: Observable<IImpersonation[]> = this.store.select<IImpersonation[]>(UserState.impersonatingUsers);
  protected user$: Observable<IUser> = this.store.select<IUser>(UserState.user);
  protected isLoading$: Signal<boolean> = this.store.selectSignal<boolean>(UserState.isLoading);

  protected dialogDataPipe: IImpersonationDialogDataPipe = {
    deletableImpersonations: [],
    addableImpersonations: [],
  };
  protected selectedTab = 0;
  protected impersonatingUsers = signal<IImpersonation[]>([]);
  protected imitableUsers: IImpersonation[];
  protected impersonationColumns: TableColumn<IImpersonation>[];
  protected impersonationColumnsWithoutDelete: TableColumn<IImpersonation>[];
  protected readonly LoadingMode = LoadingMode;

  public ngOnInit(): void {
    this.setupTableColumns();
    this.initializeImpersonationData();
  }

  protected openAssignEmployeeDialog(): void {
    const dialogRef = this.dialog.open(AssignImpersonatorComponent, {
      width: '500px',
      panelClass: 'difa-dialog',
    });
    dialogRef.disableClose = true;

    dialogRef
      .afterClosed()
      .pipe(
        tap((impersonation: IImpersonation) => {
          if (impersonation) {
            const duplicate = this.impersonatingUsers()
              .map(impersonatingUser => impersonatingUser.parentGid)
              .includes(impersonation.parentGid);

            if (!duplicate) {
              this.dialogDataPipe.addableImpersonations.push(impersonation);
              this.impersonatingUsers.update(users => [...users, impersonation]);
            }
          }
        }),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe();
  }

  protected handleTabChange(selectedTab: number): void {
    this.selectedTab = selectedTab;
  }

  public deleteEntry(entry: IImpersonation): void {
    this.impersonatingUsers.update(users => users.filter(item => item.parentGid !== entry.parentGid));
    if (this.dialogDataPipe.addableImpersonations.find(item => item.parentGid === entry.parentGid) != null) {
      this.dialogDataPipe.addableImpersonations = this.dialogDataPipe.addableImpersonations.filter(
        item => item.parentGid !== entry.parentGid,
      );
    } else {
      this.dialogDataPipe.deletableImpersonations.push(entry);
    }
  }

  protected closeWindow(): void {
    this.dialogRef.close();
  }

  protected save(): void {
    this.store.dispatch(new UpdateImpersonations(this.dialogDataPipe));
    this.dialogRef.close(this.dialogDataPipe);
  }

  private initializeImpersonationData() {
    const dispatchAction$ = this.store.dispatch(new LoadUserImpersonationData());
    dispatchAction$
      .pipe(
        concatMap(() => this.impersonatingUsers$),
        tap(impersonatingUserList => this.impersonatingUsers.set(this.filterOutExpiredImpersonations(impersonatingUserList))),
        concatMap(() => this.imitableUsers$),
        tap(imitableUsersList => (this.imitableUsers = this.filterOutExpiredImpersonations(imitableUsersList))),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe();
  }

  private setupTableColumns() {
    this.impersonationColumns = getImpersonationColumns(this.translateService, this);
    this.impersonationColumnsWithoutDelete = this.impersonationColumns.filter(column => column.name !== 'default.delete');
  }

  private filterOutExpiredImpersonations(impersonationList: IImpersonation[]): IImpersonation[] {
    return impersonationList.filter(impersonation => {
      const validTo = new Date(impersonation.validTo);
      const today = new Date();
      today.setHours(0);
      if (validTo.getTime() < today.getTime()) {
        this.dialogDataPipe.deletableImpersonations.push(impersonation);
        return false;
      }
      return true;
    });
  }
}
